import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import type { SessionId, SessionPromptId } from '@/types/Session.ts';
import { v5 as uuidv5 } from 'uuid';
import { STATIC_NAMESPACE_ID } from '@/constants/StaticNamespaceId.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { type PromptStatus } from '@/types/PromptStatus.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { VariableConfigService } from '@/services/config/VariableConfigService.ts';
import { VariableService } from '@/services/VariableService.ts';

export abstract class PromptService {
    public static getSessionPromptId(
        sessionId: SessionId,
        promptConfigId: PromptConfig['id'],
        variableChainHash: string,
    ): SessionPromptId {
        const promptConfig = PromptConfigService.getPromptConfig(promptConfigId);
        if (!promptConfig) {
            throw new Error(`Prompt config with id "${promptConfigId}" not found.`);
        }

        return uuidv5(
            `${sessionId}_${promptConfigId}_${variableChainHash}${
                promptConfig.includeScreenCapture
                    ? `_${useClientStore().screenCaptureRevisions[sessionId] ?? 0}`
                    : ''
            }`,
            STATIC_NAMESPACE_ID,
        );
    }

    public static isPromptStarted(sessionPromptId: SessionPromptId): boolean {
        const clientStore = useClientStore();

        if (clientStore.streamsWaiting.includes(sessionPromptId)) {
            return true;
        }

        if (clientStore.streamsInProgress.includes(sessionPromptId)) {
            return true;
        }

        if (clientStore.streams[sessionPromptId] !== undefined) {
            return true;
        }

        return false;
    }

    public static getPromptStatus(
        promptConfigId: PromptConfig['id'],
        sessionId: SessionId,
        variableChainHash: string,
    ): {
        status: PromptStatus;
        description: string;
    } {
        const promptConfig = PromptConfigService.getPromptConfig(promptConfigId);
        if (!promptConfig) {
            throw new Error(`Prompt config with id "${promptConfigId}" not found.`);
        }

        const sessionPromptId = PromptService.getSessionPromptId(
            sessionId,
            promptConfigId,
            variableChainHash,
        );

        const clientStore = useClientStore();

        if (clientStore.streamsErrored.includes(sessionPromptId)) {
            return {
                status: 'error',
                description:
                    clientStore.streamErrors[sessionPromptId] ??
                    'Backend communication failure. Please try again.',
            };
        }

        if (clientStore.streamsWaiting.includes(sessionPromptId)) {
            return {
                status: 'queued',
                description: 'Your prompt is queued. Please wait...',
            };
        }

        if (
            clientStore.streams[sessionPromptId] !== undefined &&
            clientStore.streamsInProgress.includes(sessionPromptId)
        ) {
            return {
                status: 'streaming',
                description:
                    'Your prompt is currently being streamed, results will be available soon.',
            };
        }

        const extractedVariableKeys = VariableService.extractVariableKeys(promptConfig.rawText);
        const hydratedVariables = VariableService.hydrateVariables(
            extractedVariableKeys,
            sessionId,
            promptConfig.profileId,
        );
        const isPromptReadyToRun = VariableService.isPromptReadyToRun(hydratedVariables);

        if (!isPromptReadyToRun) {
            return {
                status: 'waiting',
                description:
                    'Waiting for the following variables to be set: ' +
                    VariableService.getUnsetVariableKeys(hydratedVariables).join(', '),
            };
        }

        if (promptConfig.formatAs === 'choice') {
            return {
                status: 'finished',
                description: `Prompt has successfully finished, chosen option has been stored in variable: ${VariableConfigService.getPromptUserInputVariableKey(promptConfig.name)}`,
            };
        }

        return {
            status: 'finished',
            description: `Prompt has successfully finished, output has been stored in variable: ${VariableConfigService.getPromptOutputVariableKey(promptConfig.name)}`,
        };
    }
}
